import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Box, Container, Tab, Tabs } from '@material-ui/core';
import { AppColors, Section, useFormEvents, useObservable, usePageTitle } from '@lib/common';
import { AuditLogs, BreadcrumbBackButton } from 'app/shared';
import {
  CorporateAccountForms,
  corporateAccountQuery,
  corporateAccountService,
  corporateInvoiceQuery,
  corporateInvoiceService,
  propertyConfigurationQuery,
  propertyConfigurationService,
  UpdateCorporateAccountRequest,
  useGetContactsByAccountId,
} from 'app/state';
import {
  CorporateAccountBillingPolicy,
  CorporateAccountDelegates,
  CorporateAccountForm,
  CorporateAccountInvoiceList,
  CorporateAccountInvoiceSettings,
  CorporateAccountRatePlans,
  CorporateHousekeepingPolicy,
  CorporateReservationPolicy,
  CorporateZonePreferenceComponent,
} from '../components';
import CorporateContactList from '../components/corporate-contact/corporate-contact-list';
import { getCostCenterOptionsQuery, getCostCenterOptionsService, sessionQuery } from '@lib/state';
import { TabContext, TabPanel } from '@material-ui/lab';
import { useCorporateAccountDelegates } from '../useCorporateAccountDelegates';

export const EditCorporateAccountPage: React.FC<RouteComponentProps<{
  corporateAccountId: string;
}>> = ({ match }) => {
  usePageTitle('Edit Corporate Account');
  const corporateAccountId = match.params.corporateAccountId;

  const [tab, setTab] = React.useState<string>('general');

  const propertyId = useObservable(sessionQuery.propertyId, 'async');
  const account = useObservable(corporateAccountQuery.activeAccount, 'async');
  const property = useObservable(propertyConfigurationQuery.activeProperty, 'async');
  const costCenterOptions = useObservable(getCostCenterOptionsQuery().costCenterOptions, 'async');
  const invoices = useObservable(corporateInvoiceQuery.invoices);
  const pagination = useObservable(corporateInvoiceQuery.pagination);
  const delegates = useCorporateAccountDelegates(account?.id);

  const { contacts, loading: contactsLoading } = useGetContactsByAccountId(corporateAccountId);

  const [{ status, error }, reset] = useFormEvents(CorporateAccountForms.Edit);

  const defaultValues = {
    name: account?.name ?? '',
    invoiceSubAccount: account?.invoiceSubAccount ?? '',
  };

  useEffect(() => {
    if (propertyId) propertyConfigurationService.getPropertyConfiguration(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (corporateAccountId) {
      corporateAccountService.getCorporateAccountById(corporateAccountId);
      corporateInvoiceService.getInvoices(corporateAccountId);
      getCostCenterOptionsService().getCostCenterOptions(corporateAccountId);
    }
    return () => {
      getCostCenterOptionsService().reset();
      corporateAccountService.selectCorporateAccount();
    };
  }, [corporateAccountId]);

  const onSubmit = useCallback(
    ({ ...values }: UpdateCorporateAccountRequest) => {
      if (account) {
        corporateAccountService.updateCorporateAccount({
          ...account,
          ...values,
          cancelNoShows: false,
        });
      }
    },
    [account]
  );

  const loadMore = () => {
    corporateInvoiceService.getInvoices(corporateAccountId, pagination.continuationToken);
  };

  if (account == null || !defaultValues) return null;

  return (
    <Container maxWidth="lg" data-testid="corporateAccountDetailsPage">
      <BreadcrumbBackButton destination="/corporate-accounts" />
      <TabContext value={tab}>
        <Tabs
          value={tab}
          onChange={(_, x) => setTab(x)}
          indicatorColor="primary"
          textColor="primary"
          style={{ backgroundColor: AppColors.LighterGray }}
        >
          <Tab label="General" value="general" />
          <Tab label="Billing" value="billing" />
        </Tabs>
        <TabPanel value="general">
          <Box>
            <CorporateAccountForm
              account={account}
              status={status}
              error={error}
              onSubmit={onSubmit}
              resetFormUI={reset}
            />
            <CorporateHousekeepingPolicy account={account} />
            <CorporateReservationPolicy account={account} property={property} />
            <CorporateAccountRatePlans account={account} />
            <CorporateZonePreferenceComponent account={account} />
            <CorporateAccountDelegates account={account} delegates={delegates} />
            <CorporateContactList contacts={contacts} account={account} loading={contactsLoading} />
            <Section title="Audit Logs" disableGutters>
              <AuditLogs scope="corporate-account" id={account.id} />
            </Section>
          </Box>
        </TabPanel>
        <TabPanel value="billing">
          <CorporateAccountBillingPolicy account={account} defaultCostCenterOptions={costCenterOptions} />
          <CorporateAccountInvoiceSettings account={account} />
          <CorporateAccountInvoiceList
            account={account}
            invoices={invoices}
            loadMore={loadMore}
            isDone={pagination.isDone}
          />
        </TabPanel>
      </TabContext>
    </Container>
  );
};
